<template>
  <div class="relative h-full pb-4 overflow-x-auto bg-white">
    <BaseTable
      v-if="filteredSalesPersonsDC"
      :headers="salesPersonsDCHeaders"
      :list="filteredSalesPersonsDC"
      table-id="sales-persons-dc-tab"
      hide-grouping
      is-saving-layout-hidden
      :disable-multiselect="!isFromManager || mode === 'view'"
      :is-add-new-hidden="!isFromManager || mode === 'view'"
      :total-records="totalRecords"
      @add-new="onAddNew"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div
          v-if="item.isFromManager && mode === 'edit'"
          class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager && mode === 'edit'"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item)" />
        </div>
      </template>
    </BaseTable>

    <SalesPersonsDCModalAdd
      v-if="addModalActive"
      :list="availableSalesPersons"
      :total-records="availableSalesPersonsCount"
      @close="addModalActive = false"
      @refresh-data="onRefreshAvailableSalesPersons"
      @multi-select="onMultiSelect"
      @add-all-filtered="onAddAllFiltered" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import { sortData } from '@/utils/table';
import SalesPersonsDCModalAdd from '@/components/salespersonsManagement/SalesPersonsDCModalAdd.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';

import type { DistributionChannel } from '@/types/DistributionChannel';
import type { SalesPersonDC } from '@/types/SalesPersonDC';
import type { SalesPerson } from '@/types/SalesPersons';
import type { TableFilterData } from '@/types/Base';
import { RecordMode } from '@/types/Base';
import { getSalesPersons } from '@/services/api/user';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

const data = defineModel<DistributionChannel>();

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  isFromManager: boolean;
  totalRecords: number;
}>();

const emit = defineEmits<{
  'refresh-data': [filterData?: TableFilterData];
  'apply-filters': [filterData: TableFilterData];
  'reset-filters': [];
}>();

const salesPersonsDCHeaders = [
  { value: 'salesPerson.name', text: $t('salesperson.salesperson'), type: 'text', active: true },
  { value: 'recordMode', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: props.mode == 'edit' },
];

const addModalActive = ref(false);

const availableSalesPersons = ref<SalesPerson[]>([]);
const availableSalesPersonsCount = ref(0);

const filteredSalesPersonsDC = computed(() => {
  return data.value?.salesPersonsDC?.filter((x) => x.recordMode !== RecordMode.Deleted);
});

async function onAddNew() {
  if (props.isFromManager && props.mode === 'edit') {
    await loadAvailableSalesPersons();
    addModalActive.value = true;
  }
}

async function loadAvailableSalesPersons(query?: string) {
  try {
    const allSalesPersons = await getSalesPersons();

    // Filter out sales persons already added to this distribution channel
    const existingSalesPersonIds =
      data.value?.salesPersonsDC
        ?.filter((x) => x.recordMode !== RecordMode.Deleted)
        .map((x) => x.salesPersonID) || [];

    let filtered = allSalesPersons.filter((sp) => !existingSalesPersonIds.includes(sp.id));

    // Apply search query if provided
    if (query) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(
        (sp) =>
          sp.name?.toLowerCase().includes(lowerQuery) || sp.code?.toLowerCase().includes(lowerQuery)
      );
    }

    availableSalesPersons.value = filtered;
    availableSalesPersonsCount.value = filtered.length;
  } catch (error) {
    toastStore.error({ text: 'Failed to load sales persons' });
    availableSalesPersons.value = [];
    availableSalesPersonsCount.value = 0;
  }
}

function onRefreshAvailableSalesPersons(filterData: TableFilterData) {
  loadAvailableSalesPersons(filterData.query);
}

function onMultiSelect(selectedSalesPersons: SalesPerson[]) {
  if (!data.value) return;

  if (!Array.isArray(data.value.salesPersonsDC)) data.value.salesPersonsDC = [];

  selectedSalesPersons.forEach((salesPerson) => {
    if (!data.value) return;

    const newSalesPersonDC: SalesPersonDC = {
      id: uuidv4(),
      salesPersonID: salesPerson.id,
      distributionChannelID: data.value.id || '',
      distributionChannel: {
        id: data.value.id || '',
        company: data.value.company || { id: '', name: '' },
        mwAccountGroup: data.value.mwAccountGroup || { id: '', accountGroup: '', mwEntity: '' },
      },
      salesPerson: {
        id: salesPerson.id,
        name: salesPerson.name,
      },
      isActive: true,
      isFromManager: true,
      deleted: false,
      recordMode: RecordMode.Added,
    };

    if (data.value.salesPersonsDC) {
      data.value.salesPersonsDC.push(newSalesPersonDC);
    }
  });

  addModalActive.value = false;
}

async function onAddAllFiltered(isActive: boolean, query?: string) {
  if (!data.value) return;

  await loadAvailableSalesPersons(query);

  if (!Array.isArray(data.value.salesPersonsDC)) data.value.salesPersonsDC = [];

  availableSalesPersons.value.forEach((salesPerson) => {
    if (!data.value) return;

    const newSalesPersonDC: SalesPersonDC = {
      id: uuidv4(),
      salesPersonID: salesPerson.id,
      distributionChannelID: data.value.id || '',
      distributionChannel: {
        id: data.value.id || '',
        company: data.value.company || { id: '', name: '' },
        mwAccountGroup: data.value.mwAccountGroup || { id: '', accountGroup: '', mwEntity: '' },
      },
      salesPerson: {
        id: salesPerson.id,
        name: salesPerson.name,
      },
      isActive: isActive,
      isFromManager: true,
      deleted: false,
      recordMode: RecordMode.Added,
    };

    if (data.value.salesPersonsDC) {
      data.value.salesPersonsDC.push(newSalesPersonDC);
    }
  });

  addModalActive.value = false;
}

async function onDeleteMultiselect(items: SalesPersonDC[]) {
  if (props.isFromManager) {
    const itemsArray = Array.isArray(items) ? items : [items];
    const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
    if (itemsToDelete.length === 0) {
      toastStore.error({ text: 'Selected items cannot be deleted.' });
      return;
    }

    itemsToDelete.forEach(async (item) => {
      deleteRowItem.value = item;
      await onDeleteSingle();
    });
  }
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!data.value || !deleteRowItem.value) return;

  if (!Array.isArray(data.value.salesPersonsDC)) return;

  const index = data.value.salesPersonsDC.findIndex(
    (existingItem: any) => existingItem.id === deleteRowItem.value?.id
  );

  if (index !== -1) {
    const itemToDelete = data.value.salesPersonsDC[index];
    if (itemToDelete.recordMode === RecordMode.Added) {
      // Remove completely if it was just added
      data.value.salesPersonsDC.splice(index, 1);
    } else {
      // Mark as deleted if it exists in DB
      itemToDelete.recordMode = RecordMode.Deleted;
      itemToDelete.deleted = true;
    }
  }

  deleteRowItem.value = undefined;
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<SalesPersonDC>();

function onDeleteRow(item: SalesPersonDC) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onRefreshData(filterData: TableFilterData) {
  if (filterData.column !== undefined && filterData.order && filteredSalesPersonsDC.value) {
    const sorted = sortData(
      [...filteredSalesPersonsDC.value],
      filterData.column,
      filterData.order,
      salesPersonsDCHeaders
    );
    if (data.value) {
      const deletedItems =
        data.value.salesPersonsDC?.filter((x) => x.recordMode === RecordMode.Deleted) || [];
      data.value.salesPersonsDC = [...sorted, ...deletedItems];
    }
    return;
  }

  emit('refresh-data', filterData);
}

function onApplyFilters(filterData: TableFilterData) {
  emit('apply-filters', filterData);
}

function onResetFilters() {
  emit('reset-filters');
}
</script>
